import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowRight, Shield, Globe, TrendingUp, CheckCircle, Zap, HeadphonesIcon } from 'lucide-react';

export const FeaturesSection = () => {
  const { t } = useTranslation();
  
  const features = [
    { 
      icon: Shield, 
      title: t('home.features.verified'), 
      desc: t('home.features.verifiedDesc'),
      color: 'bg-emerald-500'
    },
    { 
      icon: Globe, 
      title: t('home.features.globalReach'), 
      desc: t('home.features.globalReachDesc'),
      color: 'bg-blue-500'
    },
    { 
      icon: TrendingUp, 
      title: t('home.features.tradeFinance'), 
      desc: t('home.features.tradeFinanceDesc'),
      color: 'bg-violet-500'
    },
    { 
      icon: CheckCircle, 
      title: t('home.features.qualityAssured'), 
      desc: t('home.features.qualityAssuredDesc'),
      color: 'bg-amber-500'
    },
    { 
      icon: Zap, 
      title: t('home.features.fastOnboarding'), 
      desc: t('home.features.fastOnboardingDesc'),
      color: 'bg-rose-500'
    },
    { 
      icon: HeadphonesIcon, 
      title: t('home.features.support'), 
      desc: t('home.features.supportDesc'),
      color: 'bg-cyan-500'
    }
  ];

  return (
    <section className="py-24 sm:py-32 bg-[#FAFAF9] relative overflow-hidden" data-testid="features-section">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(#0A0A0A 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
        }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Image Grid */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative order-2 lg:order-1"
          >
            <div className="grid grid-cols-12 gap-4">
              {/* Main Image */}
              <div className="col-span-7 row-span-2">
                <img
                  src="https://images.unsplash.com/photo-1655102713930-ed68081e6b7d?w=600&h=800&fit=crop"
                  alt="African commodities"
                  className="rounded-3xl shadow-2xl w-full h-[420px] object-cover"
                />
              </div>
              
              {/* Secondary Images */}
              <div className="col-span-5 space-y-4">
                <img
                  src="https://images.pexels.com/photos/6525848/pexels-photo-6525848.jpeg?w=400&h=300&fit=crop"
                  alt="Manufacturing"
                  className="rounded-2xl shadow-xl w-full h-[200px] object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=400&h=300&fit=crop"
                  alt="Shipping"
                  className="rounded-2xl shadow-xl w-full h-[200px] object-cover"
                />
              </div>
            </div>
            
            {/* Floating Stats Card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="absolute -bottom-6 -right-6 lg:right-8 bg-white rounded-2xl shadow-2xl p-6 border border-slate-100"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#C25E30] rounded-2xl flex items-center justify-center">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#0A0A0A]">54+</p>
                  <p className="text-slate-500 text-sm">{t('home.features.africanCountries')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-10 order-1 lg:order-2"
          >
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C25E30] mb-4">
                {t('home.features.whyChoose')}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] tracking-tight mb-5">
                {t('home.features.trustedGateway')}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C25E30] to-[#E8A54B]">
                  {t('home.features.africanTrade')}
                </span>
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed">
                {t('home.features.description')}
              </p>
            </div>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.4 }}
                  className="group bg-white p-5 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100 hover:border-[#C25E30]/20"
                >
                  <div className={`w-10 h-10 ${feature.color} rounded-xl flex items-center justify-center mb-3 shadow-sm`}>
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#0A0A0A] mb-1 group-hover:text-[#C25E30] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <Link to="/register">
                <Button 
                  className="bg-[#C25E30] hover:bg-[#A64D24] text-white btn-premium gap-3 px-8 py-6 text-base font-semibold rounded-xl" 
                  size="lg" 
                  data-testid="features-register-btn"
                >
                  {t('home.features.joinToday')} <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
